<template>
  <v-slide-y-transition mode="out-in">
    <v-alert
      v-model="display"
      :icon="icon"
      :color="color"
      :dismissible="dismissible"
      class="custom-alert"
      :class="$desktopModeStyles"
      @click.stop="onClick"
    >
      <v-layout justify-center>
        <div class="custom-alert-content">
          <vue-markdown
            :hjsuppress="hjsuppress"
            :source="translatedMessage"
            class="vue-markdown-parent"
          />
        </div>
        <!-- Optional route link. -->
        <span v-if="link">
          &nbsp;
          <span
            class="pseudo-anchor-white"
            @click.stop="onLinkClicked(link)"
          >{{
            link.text
          }}</span>
        </span>
      </v-layout>
    </v-alert>
  </v-slide-y-transition>
</template>

<script>
import VueMarkdown from 'vue-markdown';

import {redirectToExternalUrl} from '@/utils';

export default {
  components: {
    VueMarkdown,
  },
  props: {
    /* Unique identifier (e.g. for removal in parent widget). */
    id: {
      type: Number,
      required: true,
    },
    /* Message text of the alert body. */
    message: {
      type: String,
      required: true,
    },
    /* Optionally override the default icon. */
    icon: {
      type: String,
      default: null,
    },
    /**
     * Whether the message is a key that we can use for translating.
     *
     * If the message contains markdown, please translate needed text
     * beforehand and set this to false, otherwise we'd pass markdown
     * to the i18n module which won't understand it.
     */
    translate: {
      type: Boolean,
      default: false,
    },
    /* Timeout in milliseconds (0 or less means 'forever'). */
    timeout: {
      type: Number,
      default: 3000,
    },
    /* Whether to show an 'x' to close the dialog. */
    dismissible: {
      type: Boolean,
      default: true,
    },
    /* Style to use for coloring (info, success, warning, error). */
    color: {
      type: String,
      default: 'info',
    },
    /**
     * Optional object to append a link to an internal route or external URL.
     *
     * link (Object):
     *   - text (String): Label to render the <a> with.
     *   - path (String): Route path or absolute URL for external links.
     *   - external (Boolean): Optional, false by default. If true, will
     *     do a clientside redirect to the URL instead of in-app routing.
     */
    link: {
      type: Object,
      default: null,
    },
    /* When on, suppress this alert's text in hotjar/mouseflow. */
    hjsuppress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      display: true,
    };
  },
  computed: {
    translatedMessage() {
      if (this.translate) {
        return this.$t(this.message);
      }
      return this.message;
    },
  },
  mounted() {
    var vm = this;

    if (this.timeout > 0) {
      setTimeout(() => {
        vm.remove();
      }, this.timeout);
    }
  },
  methods: {
    /**
     * Handle the click event separately, because we don't want it to
     * be handled by the v-alert, which might close off opened
     * v-dialogs, since the click registers as 'you clicked outside
     * the dialog' events.
     */
    onClick(event) {
      if (!this.dismissible) {
        return;
      }
      this.remove();
    },
    onLinkClicked(link) {
      if (link.external) {
        redirectToExternalUrl(this, link.path);
      } else {
        this.$router.push({path: link.path});
      }
    },
    remove() {
      this.$bus.$emit('remove-alert', this.id);
    },
  },
};
</script>

<style>
/* This is needed so that the below ellipsis rule works. */
.custom-alert.alert > div {
  overflow: hidden;
}
</style>

<style scoped>
/* Fix automatic p tag comes with unwanted bottom margin. */
.vue-markdown-parent >>> p {
  margin-bottom: 0;
}

/* Fix anchors use --msr-color-primary which looks bad on saturated bg color. */
.custom-alert-content >>> a {
  color: white;
}

.custom-alert {
  margin: 0;
}

.custom-alert-content {
  overflow: hidden;
  text-overflow: ellipsis;
}

.pseudo-anchor-white {
  color: white;
  text-decoration: underline;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/Alert.vue